import React, { useMemo, useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { X, Share, Loader2, Camera, Video, ArrowUp, ArrowDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { DishGalleryContent } from '@/components/gallery/DishGalleryContent';
import { DishStatsBar } from '@/components/gallery/DishStatsBar';
import { InfoSheet } from '@/components/ui/InfoSheet';
import { getDietaryIcon, getAllergenIcon } from '@/utils/iconMaps';
import { toast } from 'sonner';
import { formatPrice } from '@/utils/currencyUtils';
import { useLocation } from '@/contexts/LocationContext';
import { calculateDistance, formatDistance } from '@/utils/distanceUtils';
import { CreateSnapSheet } from '@/components/snap/CreateSnapSheet';
import { CreateDroolSheet } from '@/components/drool/CreateDroolSheet';
import { shareDish } from '@/utils/shareUtils';
import { useAuth } from '@/contexts/AuthContext';
import { formatCompactNumber } from '@/utils/formatNumber';
import { trackEvent } from '@/services/analyticsService';

interface DishSheetProps {
  dishId: string | null;
  onClose: () => void;
}

export const DishSheet: React.FC<DishSheetProps> = ({ dishId, onClose }) => {
  const [isInfoSheetOpen, setIsInfoSheetOpen] = useState(false);
  const [showSnapSheet, setShowSnapSheet] = useState(false);
  const [showDroolSheet, setShowDroolSheet] = useState(false);
  const dragControls = useDragControls();
  const navigate = useNavigate();
  const isOpen = dishId !== null;
  const { activeLocation, settings } = useLocation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Local vote state for instant UI feedback
  const [localVoteDirection, setLocalVoteDirection] = useState<'up' | 'down' | null>(null);
  const [localUpvotes, setLocalUpvotes] = useState(0);
  const [localDownvotes, setLocalDownvotes] = useState(0);

  // Determine if dishId is a UUID at component scope so the tags query can start in parallel
  const isUUID = dishId ? /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(dishId) : false;

  const { data: dish, isLoading } = useQuery({
    queryKey: ['dish-metadata', dishId],
    queryFn: async () => {
      if (!dishId) return null;

      let query = supabase.from('dishes').select(`
        id,
        name,
        slug,
        submitted_by,
        price,
        chef,
        description,
        image_url,
        cover_image_url,
        restaurants (id, name, country_code, latitude, longitude, google_place_id, map_url),
        profiles!dishes_submitted_by_fkey (id, username, avatar_url, user_stats(dish_submissions)),
        dish_stats (
          top_taste, top_portion, top_value, total_reviews,
          taste_count, portion_count, value_count,
          min_price, max_price, avg_price
        ),
        dish_rating_distribution (
          total_reviews,
          taste_solid_count, taste_fire_count, taste_life_changing_count, taste_meh_count,
          portion_just_right_count, portion_snack_attack_count, portion_food_coma_count,
          value_fair_deal_count, value_steal_count, value_splurge_count, value_daylight_robbery_count
        ),
        dish_allergens (allergens (id, name, icon)),
        dish_claims (dietary_claims (id, name, icon))
      `);

      if (isUUID) {
        query = query.eq('id', dishId);
      } else {
        query = query.eq('slug', dishId);
      }

      const { data, error } = await query.maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!dishId,
  });

  // Fetch vote counts + current user's vote direction
  const { data: voteData } = useQuery({
    queryKey: ['dish-votes', dish?.id ?? dishId, user?.id],
    queryFn: async () => {
      const id = dish?.id ?? (isUUID ? dishId : null);
      if (!id) return null;

      const [votesResult, userVoteResult] = await Promise.all([
        supabase.from('dishes').select('upvotes, downvotes').eq('id', id).maybeSingle(),
        user
          ? supabase.from('votes').select('vote_type').eq('dish_id', id).eq('user_id', user.id).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);

      return {
        upvotes: votesResult.data?.upvotes ?? 0,
        downvotes: votesResult.data?.downvotes ?? 0,
        userVoteDirection: (userVoteResult.data?.vote_type as 'up' | 'down' | null) ?? null,
      };
    },
    enabled: !!(dish?.id ?? (isUUID ? dishId : null)),
  });

  // Sync local vote state when remote data arrives
  useEffect(() => {
    if (voteData) {
      setLocalUpvotes(voteData.upvotes);
      setLocalDownvotes(voteData.downvotes);
      setLocalVoteDirection(voteData.userVoteDirection);
    }
  }, [voteData]);

  // Vote mutation (same pattern as InteractiveDishCard)
  const voteMutation = useMutation({
    mutationFn: async ({ voteDirection }: { voteDirection: 'up' | 'down'; currentVote: 'up' | 'down' | null; currentUpvotes: number; currentDownvotes: number }) => {
      const { data, error } = await supabase.rpc('handle_vote', {
        item_id_in: dish!.id,
        item_type_in: 'dish',
        vote_direction_in: voteDirection,
      });
      if (error) throw error;
      return data;
    },
    onMutate: async ({ voteDirection, currentVote, currentUpvotes, currentDownvotes }) => {
      const itemId = dish!.id;

      await queryClient.cancelQueries({ queryKey: ['chartFeed'] });
      await queryClient.cancelQueries({ queryKey: ['whatsNew'] });
      await queryClient.cancelQueries({ queryKey: ['global-top-100'] });
      await queryClient.cancelQueries({ queryKey: ['user-profile-content'] });

      const previousChartFeed = queryClient.getQueriesData({ queryKey: ['chartFeed'] });
      const previousWhatsNew = queryClient.getQueriesData({ queryKey: ['whatsNew'] });
      const previousGlobalTop100 = queryClient.getQueriesData({ queryKey: ['global-top-100'] });
      const previousProfileContent = queryClient.getQueriesData({ queryKey: ['user-profile-content'] });

      let newUpvotes = currentUpvotes;
      let newDownvotes = currentDownvotes;
      let newUserVoteDirection: 'up' | 'down' | null = null;

      if (!currentVote) {
        if (voteDirection === 'up') { newUpvotes += 1; newUserVoteDirection = 'up'; }
        else { newDownvotes += 1; newUserVoteDirection = 'down'; }
      } else if (currentVote === voteDirection) {
        if (voteDirection === 'up') { newUpvotes -= 1; } else { newDownvotes -= 1; }
        newUserVoteDirection = null;
      } else {
        if (currentVote === 'up') { newUpvotes -= 1; newDownvotes += 1; }
        else { newUpvotes += 1; newDownvotes -= 1; }
        newUserVoteDirection = voteDirection as 'up' | 'down';
      }

      const newNetScore = newUpvotes - newDownvotes;

      const updateFunction = (old: any) => {
        if (!old) return old;
        if (Array.isArray(old)) {
          return old.map((item: any) =>
            item.id === itemId
              ? { ...item, upvotes: newUpvotes, downvotes: newDownvotes, net_score: newNetScore, user_vote_direction: newUserVoteDirection }
              : item
          );
        }
        if (old.pages) {
          return {
            ...old,
            pages: old.pages.map((page: any) => {
              if (page?.dishes && Array.isArray(page.dishes)) {
                return { ...page, dishes: page.dishes.map((item: any) => item.id === itemId ? { ...item, upvotes: newUpvotes, downvotes: newDownvotes, net_score: newNetScore, user_vote_direction: newUserVoteDirection } : item) };
              }
              if (Array.isArray(page)) {
                return page.map((item: any) => item.id === itemId ? { ...item, upvotes: newUpvotes, downvotes: newDownvotes, net_score: newNetScore, user_vote_direction: newUserVoteDirection } : item);
              }
              return page;
            }),
          };
        }
        return old;
      };

      queryClient.setQueriesData({ queryKey: ['chartFeed'] }, updateFunction);
      queryClient.setQueriesData({ queryKey: ['whatsNew'] }, updateFunction);
      queryClient.setQueriesData({ queryKey: ['global-top-100'] }, updateFunction);
      queryClient.setQueriesData({ queryKey: ['user-profile-content'] }, updateFunction);

      return { previousChartFeed, previousWhatsNew, previousGlobalTop100, previousProfileContent };
    },
    onError: (_err, _variables, context) => {
      // Revert feed cache
      if (context?.previousChartFeed) {
        context.previousChartFeed.forEach(([queryKey, data]: [any, any]) => queryClient.setQueryData(queryKey, data));
      }
      if (context?.previousWhatsNew) {
        context.previousWhatsNew.forEach(([queryKey, data]: [any, any]) => queryClient.setQueryData(queryKey, data));
      }
      if (context?.previousGlobalTop100) {
        context.previousGlobalTop100.forEach(([queryKey, data]: [any, any]) => queryClient.setQueryData(queryKey, data));
      }
      if (context?.previousProfileContent) {
        context.previousProfileContent.forEach(([queryKey, data]: [any, any]) => queryClient.setQueryData(queryKey, data));
      }
      // Revert local state
      if (voteData) {
        setLocalUpvotes(voteData.upvotes);
        setLocalDownvotes(voteData.downvotes);
        setLocalVoteDirection(voteData.userVoteDirection);
      }
      toast.error('Vote failed. Please try again.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['dish-votes'],     refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['chartFeed'],      refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['whatsNew'],       refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['global-top-100'], refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['user-votes'],     refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['user-profile-content'], refetchType: 'none' });
    },
  });

  const handleUpvote = () => {
    if (!user) { toast.info('Please sign in to vote!'); return; }
    trackEvent('Dish Engagement', 'Upvote', dish?.name ?? '');
    const capturedVote = localVoteDirection;
    const capturedUpvotes = localUpvotes;
    const capturedDownvotes = localDownvotes;
    let newUp = localUpvotes, newDown = localDownvotes, newDir: 'up' | 'down' | null = null;
    if (!capturedVote) { newUp += 1; newDir = 'up'; }
    else if (capturedVote === 'up') { newUp -= 1; newDir = null; }
    else { newUp += 1; newDown -= 1; newDir = 'up'; }
    setLocalUpvotes(newUp); setLocalDownvotes(newDown); setLocalVoteDirection(newDir);
    voteMutation.mutate({ voteDirection: 'up', currentVote: capturedVote, currentUpvotes: capturedUpvotes, currentDownvotes: capturedDownvotes });
  };

  const handleDownvote = () => {
    if (!user) { toast.info('Please sign in to vote!'); return; }
    trackEvent('Dish Engagement', 'Downvote', dish?.name ?? '');
    const capturedVote = localVoteDirection;
    const capturedUpvotes = localUpvotes;
    const capturedDownvotes = localDownvotes;
    let newUp = localUpvotes, newDown = localDownvotes, newDir: 'up' | 'down' | null = null;
    if (!capturedVote) { newDown += 1; newDir = 'down'; }
    else if (capturedVote === 'down') { newDown -= 1; newDir = null; }
    else { newUp -= 1; newDown += 1; newDir = 'down'; }
    setLocalUpvotes(newUp); setLocalDownvotes(newDown); setLocalVoteDirection(newDir);
    voteMutation.mutate({ voteDirection: 'down', currentVote: capturedVote, currentUpvotes: capturedUpvotes, currentDownvotes: capturedDownvotes });
  };

  // For UUID dish IDs (all in-app navigation), start tags query in parallel with dish metadata.
  // For slug-based URLs (external links), fall back to dish?.id once the first query resolves.
  const effectiveDishId = isUUID ? dishId : dish?.id;

  const { data: topTags = [] } = useQuery({
    queryKey: ['dish-top-tags', effectiveDishId],
    queryFn: async () => {
      if (!effectiveDishId) return [];
      const { data, error } = await supabase
        .from('posts')
        .select('tags')
        .eq('dish_id', effectiveDishId)
        .not('tags', 'is', null);
      if (error) throw error;
      const tagCounts: Record<string, number> = {};
      data?.forEach(post => {
        post.tags?.forEach((tag: string) => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      });
      return Object.entries(tagCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([tag]) => tag);
    },
    enabled: !!effectiveDishId,
  });

  const dishStats = useMemo(() => {
    if (!dish?.dish_stats || !Array.isArray(dish.dish_stats)) return null;
    return dish.dish_stats[0] || null;
  }, [dish?.dish_stats]);

  const dishDistribution = useMemo(() => {
    if (!dish?.dish_rating_distribution || !Array.isArray(dish.dish_rating_distribution)) return null;
    return dish.dish_rating_distribution[0] || null;
  }, [dish?.dish_rating_distribution]);

  const distance = useMemo(() => {
    const lat = dish?.restaurants?.latitude;
    const lng = dish?.restaurants?.longitude;
    if (!lat || !lng) return null;
    const d = calculateDistance(activeLocation.latitude, activeLocation.longitude, lat, lng);
    return formatDistance(d, settings.unit);
  }, [dish?.restaurants?.latitude, dish?.restaurants?.longitude, activeLocation, settings.unit]);

  const handleShare = async () => {
    await shareDish({
      name: dish?.name || 'Check out this dish!',
      slug: dish?.slug,
      id: dish?.id || dishId,
      restaurant: dish?.restaurants?.name,
    });
  };

  const hasAllergenInfo =
    (dish?.dish_claims && dish.dish_claims.length > 0) ||
    (dish?.dish_allergens && dish.dish_allergens.length > 0);

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <AnimatePresence>
          {isOpen && (
            <Dialog.Portal forceMount>
              <Dialog.Overlay asChild>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/60 z-50"
                />
              </Dialog.Overlay>
              <Dialog.Content asChild>
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: '0%' }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'tween', ease: 'easeInOut', duration: 0.3 }}
                  drag="y"
                  dragControls={dragControls}
                  dragListener={false}
                  dragConstraints={{ top: 0 }}
                  dragElastic={{ top: 0, bottom: 0.4 }}
                  onDragEnd={(_, info) => { if (info.offset.y > 120) onClose(); }}
                  className="fixed bottom-0 left-0 right-0 h-[90vh] bg-background z-50 rounded-t-2xl flex flex-col overflow-hidden"
                >
                  {/* Drag handle */}
                  <div
                    className="flex-shrink-0 pt-3 pb-1 flex justify-center touch-none cursor-grab active:cursor-grabbing"
                    onPointerDown={(e) => dragControls.start(e)}
                  >
                    <div className="w-10 h-1.5 bg-border rounded-full" />
                  </div>

                  {/* Header */}
                  <header
                    className="flex-shrink-0 touch-none cursor-grab active:cursor-grabbing"
                    onPointerDown={(e) => dragControls.start(e)}
                  >
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <Dialog.Title className="text-lg font-bold text-foreground truncate">
                          {dish?.name ?? ''}
                        </Dialog.Title>
                        {dish?.restaurants?.name && dish?.restaurants?.id && (
                          <button
                            onClick={() => { onClose(); navigate(`/restaurant/${dish.restaurants!.id}`); }}
                            className="text-sm text-muted-foreground truncate hover:text-foreground hover:underline text-left block"
                          >
                            {dish.restaurants.name}
                          </button>
                        )}
                        {distance && (
                          <p className="text-xs text-muted-foreground">{distance} away</p>
                        )}
                        {/* Vote pill */}
                        <div className="flex items-center h-8 bg-muted border border-border rounded-full overflow-hidden w-fit mt-2">
                          <button
                            onClick={handleUpvote}
                            disabled={voteMutation.isPending}
                            className={`h-full flex items-center pl-3 pr-2 transition-colors duration-200 rounded-l-full ${
                              localVoteDirection === 'up' ? 'bg-[#cfef00]' : 'hover:bg-muted-foreground/20'
                            }`}
                            aria-label="Upvote"
                          >
                            <ArrowUp className={`w-4 h-4 mr-1 ${localVoteDirection === 'up' ? 'text-black' : 'text-muted-foreground'}`} />
                            <span className={`font-bold text-xs tabular-nums ${localVoteDirection === 'up' ? 'text-black' : 'text-foreground'}`}>
                              {formatCompactNumber(localUpvotes)}
                            </span>
                          </button>
                          <div className="w-px h-5 bg-border" />
                          <button
                            onClick={handleDownvote}
                            disabled={voteMutation.isPending}
                            className={`h-full flex items-center pl-2 pr-3 transition-colors duration-200 rounded-r-full ${
                              localVoteDirection === 'down' ? 'bg-red-500' : 'hover:bg-muted-foreground/20'
                            }`}
                            aria-label="Downvote"
                          >
                            <span className={`font-bold text-xs tabular-nums ${localVoteDirection === 'down' ? 'text-white' : 'text-foreground'}`}>
                              {formatCompactNumber(localDownvotes)}
                            </span>
                            <ArrowDown className={`w-4 h-4 ml-1 ${localVoteDirection === 'down' ? 'text-white' : 'text-muted-foreground'}`} />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={handleShare}
                          className="text-muted-foreground hover:text-foreground hover:bg-muted"
                          aria-label="Share dish"
                        >
                          <Share className="h-5 w-5" />
                        </Button>
                        <Dialog.Close asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-foreground hover:bg-muted"
                            aria-label="Close"
                          >
                            <X className="h-5 w-5" />
                          </Button>
                        </Dialog.Close>
                      </div>
                    </div>
                  </header>

                  {/* Body */}
                  {isLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : dish ? (
                    <div className="flex-1 overflow-y-auto">
                      {/* Dish details */}
                      {(dish.price || dish.chef || dish.description || (dishStats as any)?.min_price) && (
                        <div className="px-4 py-3 space-y-2">
                          {((dishStats as any)?.min_price || dish.price) && (
                            <div className="flex items-center text-base">
                              <span className="text-muted-foreground mr-2">Price:</span>
                              <span className="text-foreground font-semibold">
                                {(dishStats as any)?.min_price &&
                                (dishStats as any)?.max_price &&
                                (dishStats as any).min_price !== (dishStats as any).max_price
                                  ? `${formatPrice((dishStats as any).min_price, dish.restaurants?.country_code)} - ${formatPrice((dishStats as any).max_price, dish.restaurants?.country_code)}`
                                  : formatPrice(
                                      dish.price || (dishStats as any)?.min_price,
                                      dish.restaurants?.country_code
                                    )}
                              </span>
                              {(dishStats as any)?.min_price &&
                                (dishStats as any)?.max_price &&
                                (dishStats as any).min_price !== (dishStats as any).max_price && (
                                  <span className="text-muted-foreground text-xs ml-2">(community reported)</span>
                                )}
                            </div>
                          )}
                          {dish.chef && (
                            <div className="flex items-center text-base">
                              <span className="text-muted-foreground mr-2">Chef's Special:</span>
                              <span className="text-foreground font-semibold">{dish.chef}</span>
                            </div>
                          )}
                          {dish.description && (
                            <p className="text-muted-foreground text-sm">{dish.description}</p>
                          )}
                        </div>
                      )}

                      {/* Dietary claims & allergens */}
                      {hasAllergenInfo && (
                        <div className="px-4 py-3 flex flex-col gap-2">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Dietary Restrictions</p>
                          <div className="flex flex-wrap gap-2">
                          {dish.dish_claims?.map(({ dietary_claims }) => (
                            <div
                              key={dietary_claims.id}
                              className="flex items-center gap-1.5 bg-muted border border-border text-foreground px-2.5 py-1 rounded-full text-xs"
                            >
                              <span className="shrink-0 text-foreground">
                                {getDietaryIcon(dietary_claims.icon, 'h-3.5 w-3.5')}
                              </span>
                              <span>{dietary_claims.name}</span>
                            </div>
                          ))}
                          {dish.dish_allergens?.map(({ allergens }) => (
                            <div
                              key={allergens.id}
                              className="flex items-center gap-1.5 bg-muted border border-border text-foreground px-2.5 py-1 rounded-full text-xs"
                            >
                              <span className="shrink-0">
                                {getAllergenIcon(allergens.icon, 'h-3.5 w-3.5')}
                              </span>
                              <span>{allergens.name}</span>
                            </div>
                          ))}
                          </div>
                        </div>
                      )}

                      {/* Stats + Gallery */}
                      <div className="bg-background flex flex-col">
                        <DishStatsBar stats={dishStats} topTags={topTags} distribution={dishDistribution} />
                        <DishGalleryContent dishId={dish.id} dishName={dish.name} disableInternalScroll />
                      </div>

                      {/* Snap / Drool CTA buttons */}
                      <div className="px-4 py-3 flex flex-row gap-2">
                        <button
                          onClick={() => setShowSnapSheet(true)}
                          className="flex items-center justify-center gap-2 w-full rounded-xl py-2.5 text-sm font-medium bg-sky-500/15 text-sky-500 hover:bg-sky-500/25 transition-colors"
                        >
                          <Camera className="h-4 w-4" />
                          Snap this!
                        </button>
                        <button
                          onClick={() => setShowDroolSheet(true)}
                          className="flex items-center justify-center gap-2 w-full rounded-xl py-2.5 text-sm font-medium bg-violet-500/15 text-violet-500 hover:bg-violet-500/25 transition-colors"
                        >
                          <Video className="h-4 w-4" />
                          Drool this!
                        </button>
                      </div>
                    </div>
                  ) : null}
                </motion.div>
              </Dialog.Content>
            </Dialog.Portal>
          )}
        </AnimatePresence>
      </Dialog.Root>

      {/* Snap / Drool sheets (pre-filled with this dish) */}
      {dish && (
        <>
          <CreateSnapSheet
            isOpen={showSnapSheet}
            onClose={() => setShowSnapSheet(false)}
            onSuccess={() => setShowSnapSheet(false)}
            initialLinkedDish={{
              id: dish.id,
              name: dish.name,
              restaurantName: dish.restaurants?.name,
              imageUrl: dish.cover_image_url ?? dish.image_url,
            }}
          />
          <CreateDroolSheet
            isOpen={showDroolSheet}
            onClose={() => setShowDroolSheet(false)}
            onSuccess={() => setShowDroolSheet(false)}
            initialLinkedDish={{
              id: dish.id,
              name: dish.name,
              restaurantName: dish.restaurants?.name,
              imageUrl: dish.cover_image_url ?? dish.image_url,
            }}
          />
        </>
      )}

      {/* Dietary info sheet (nested) */}
      {dish && (
        <InfoSheet isOpen={isInfoSheetOpen} onOpenChange={setIsInfoSheetOpen} title="Dietary Information">
          <div className="space-y-6 pb-4">
            {dish.dish_claims && dish.dish_claims.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold uppercase text-white/60 tracking-wider">
                  Dietary Claims
                </h3>
                <div className="flex flex-wrap gap-2">
                  {dish.dish_claims.map(({ dietary_claims }) => (
                    <div
                      key={dietary_claims.id}
                      className="flex items-center gap-2 bg-white/10 border border-white/20 text-white px-3 py-1.5 rounded-full text-sm"
                    >
                      <span className="shrink-0 text-white">
                        {getDietaryIcon(dietary_claims.icon, 'h-4 w-4')}
                      </span>
                      <span>{dietary_claims.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {dish.dish_allergens && dish.dish_allergens.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold uppercase text-white/60 tracking-wider">
                  Contains Allergens
                </h3>
                <div className="flex flex-wrap gap-2">
                  {dish.dish_allergens.map(({ allergens }) => (
                    <div
                      key={allergens.id}
                      className="flex items-center gap-2 bg-white/10 border border-white/20 text-white px-3 py-1.5 rounded-full text-sm"
                    >
                      <span className="shrink-0 text-white">
                        {getAllergenIcon(allergens.icon, 'h-4 w-4')}
                      </span>
                      <span>{allergens.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </InfoSheet>
      )}
    </>
  );
};
